import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  MOCK_EMR_KEY,
  MOCK_EMR_PRESETS,
  getMockEMRData,
  setMockEMRData,
} from '../utils/mockEMR.js';

const PRESET_KEYS = Object.keys(MOCK_EMR_PRESETS);
const DEFAULT_PRESET = 'generic';

const blankQuestion = () => ({
  Key: '',
  Question: '',
  Answer: '',
  Type: 'Standard',
  ReplyType: 'overwrite',
});

const normalizeQuestion = (q) => ({
  Key: q.Key ?? '',
  Question: q.Question ?? '',
  Answer: q.Answer ?? '',
  Type: q.Type ?? 'Standard',
  ReplyType: q.ReplyType === undefined ? 'overwrite' : q.ReplyType,
  ...(q.Choices ? { Choices: q.Choices } : {}),
  ...(q.ExistingNote !== undefined ? { ExistingNote: q.ExistingNote } : {}),
  ...(q.Structure ? { Structure: q.Structure } : {}),
});

const presetToState = (preset) => ({
  emrId: preset.patientInfo['EMR ID'] ?? '',
  firstName: preset.patientInfo['First Name'] ?? '',
  lastName: preset.patientInfo['Last Name'] ?? '',
  dob: preset.patientInfo['DOB'] ?? '',
  metadata: preset.patientInfo['Metadata'] ?? '',
  url: preset.pageInfo['URL'] ?? '',
  noteType: preset.pageInfo['Note Type'] ?? '',
  questions: (preset.questions?.questions ?? []).map(normalizeQuestion),
});

const stateToMockData = (enabled, s) => ({
  enabled,
  patientInfo: {
    'EMR ID': s.emrId,
    'First Name': s.firstName,
    'Last Name': s.lastName,
    DOB: s.dob,
    Metadata: s.metadata,
  },
  pageInfo: {
    URL: s.url,
    'Note Type': s.noteType,
  },
  questions: {
    questions: s.questions.map(normalizeQuestion),
    dropdownCache: {},
    dropdownMisses: {},
  },
});

const field = (label, value, onChange, placeholder = '') => (
  <FormGroup className="mb-2">
    <Label
      className="mb-1 small font-weight-bold text-uppercase text-muted"
      style={{ letterSpacing: '0.05em' }}
    >
      {label}
    </Label>
    <Input
      bsSize="sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </FormGroup>
);

const MockEMRDevTools = () => {
  const navigate = useNavigate();
  const defaultPreset = MOCK_EMR_PRESETS[DEFAULT_PRESET];

  const [enabled, setEnabled] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(DEFAULT_PRESET);
  const [s, setS] = useState(() => presetToState(defaultPreset));

  // Load from sessionStorage on mount
  useEffect(() => {
    const existing = getMockEMRData();
    if (!existing) return;
    setEnabled(!!existing.enabled);
    setS(
      presetToState({
        patientInfo: existing.patientInfo ?? defaultPreset.patientInfo,
        pageInfo: existing.pageInfo ?? defaultPreset.pageInfo,
        questions: existing.questions ?? defaultPreset.questions,
      }),
    );
  }, []);

  // Auto-save on every change
  useEffect(() => {
    setMockEMRData(stateToMockData(enabled, s));
  }, [enabled, s]);

  const set = useCallback(
    (key, value) => setS((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const loadPreset = (key) => {
    const preset = MOCK_EMR_PRESETS[key];
    if (!preset) return;
    setSelectedPreset(key);
    setS(presetToState(preset));
  };

  const updateQuestion = (idx, key, value) => {
    setS((prev) => {
      const qs = [...prev.questions];
      qs[idx] = { ...qs[idx], [key]: value };
      return { ...prev, questions: qs };
    });
  };

  const addQuestion = () =>
    setS((prev) => ({
      ...prev,
      questions: [...prev.questions, blankQuestion()],
    }));

  const removeQuestion = (idx) =>
    setS((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));

  const handleGoToCX = () => {
    setEnabled(true);
    setMockEMRData(stateToMockData(true, s));
    navigate('/User/conversations/details');
  };

  const handleClear = () => {
    setMockEMRData(null);
    setEnabled(false);
    setSelectedPreset(DEFAULT_PRESET);
    setS(presetToState(defaultPreset));
  };

  const handleDownload = () => {
    const data = stateToMockData(enabled, s);
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mock-emr-${s.lastName || 'patient'}.cmockinj`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        setEnabled(!!data.enabled);
        setSelectedPreset('');
        setS(
          presetToState({
            patientInfo: data.patientInfo ?? defaultPreset.patientInfo,
            pageInfo: data.pageInfo ?? defaultPreset.pageInfo,
            questions: data.questions ?? defaultPreset.questions,
          }),
        );
      } catch {
        alert('Invalid .cmockinj file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const sectionLabel = (text) => (
    <div
      className="text-uppercase text-muted small font-weight-bold mb-2 mt-3"
      style={{
        letterSpacing: '0.08em',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '4px',
      }}
    >
      {text}
    </div>
  );

  return (
    <>
      <Card className="shadow mb-4">
        <CardHeader className="border-0 d-flex align-items-center justify-content-between py-3">
          <div>
            <h3 className="mb-0">Mock EMR Dev Tools</h3>
            <small className="text-muted">
              Auto-saved to <code>sessionStorage["{MOCK_EMR_KEY}"]</code>
            </small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span
              className={`badge badge-lg ${enabled ? 'badge-success' : 'badge-secondary'}`}
              style={{
                fontSize: '0.85rem',
                padding: '6px 12px',
                cursor: 'pointer',
              }}
              onClick={() => setEnabled((v) => !v)}
              title="Click to toggle"
            >
              {enabled ? 'MOCK ON' : 'MOCK OFF'}
            </span>
          </div>
        </CardHeader>

        <CardBody className="pt-0">
          {/* Controls row */}
          <Row className="align-items-center mb-3 mt-2">
            <Col md={4}>
              <Label
                className="mb-1 small font-weight-bold text-uppercase text-muted"
                style={{ letterSpacing: '0.05em' }}
              >
                Preset
              </Label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedPreset}
                onChange={(e) => loadPreset(e.target.value)}
              >
                {PRESET_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {MOCK_EMR_PRESETS[key].label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col
              md={8}
              className="d-flex align-items-end"
              style={{ gap: '8px', paddingTop: '22px', flexWrap: 'wrap' }}
            >
              <Button color="success" size="sm" onClick={handleGoToCX}>
                Go to CX View
              </Button>
              <Button color="info" size="sm" outline onClick={handleDownload}>
                Download .cmockinj
              </Button>
              <label
                className="btn btn-outline-info btn-sm mb-0"
                style={{ cursor: 'pointer' }}
              >
                Load .cmockinj
                <input
                  type="file"
                  accept=".cmockinj"
                  style={{ display: 'none' }}
                  onChange={handleLoadFile}
                />
              </label>
              <Button color="secondary" size="sm" outline onClick={handleClear}>
                Clear
              </Button>
            </Col>
          </Row>

          <hr className="mt-0 mb-2" />

          {/* Patient Info */}
          {sectionLabel('Patient Info — getPatientInfo()')}
          <Row>
            <Col md={2}>
              {field('EMR ID', s.emrId, (v) => set('emrId', v), 'PT-00001')}
            </Col>
            <Col md={2}>
              {field('First Name', s.firstName, (v) => set('firstName', v))}
            </Col>
            <Col md={2}>
              {field('Last Name', s.lastName, (v) => set('lastName', v))}
            </Col>
            <Col md={2}>
              {field('DOB', s.dob, (v) => set('dob', v), 'YYYY-MM-DD')}
            </Col>
            <Col md={4}>
              {field(
                'Metadata',
                s.metadata,
                (v) => set('metadata', v),
                'e.g. Insurance: BlueCross',
              )}
            </Col>
          </Row>

          {/* Page Info */}
          {sectionLabel('Page Info — getPageInfo()')}
          <Row>
            <Col md={6}>
              {field('URL', s.url, (v) => set('url', v), 'app.webpt.com')}
            </Col>
            <Col md={6}>
              {field(
                'Note Type',
                s.noteType,
                (v) => set('noteType', v),
                'SOAP Note',
              )}
            </Col>
          </Row>

          {/* Questions */}
          {sectionLabel('Questions — getQuestionsFromEMR()')}
          {s.questions.map((q, idx) => (
            <div
              key={idx}
              className="d-flex align-items-center mb-2"
              style={{ gap: '8px' }}
            >
              <Input
                bsSize="sm"
                placeholder="Question name (used as label and key)"
                value={q.Question}
                onChange={(e) => {
                  const v = e.target.value;
                  setS((prev) => {
                    const qs = [...prev.questions];
                    qs[idx] = { ...qs[idx], Question: v, Key: v };
                    return { ...prev, questions: qs };
                  });
                }}
              />
              <Button
                close
                size="sm"
                onClick={() => removeQuestion(idx)}
                title="Remove"
              />
            </div>
          ))}
          <Button color="primary" size="sm" outline onClick={addQuestion}>
            + Add Question
          </Button>
        </CardBody>
      </Card>
    </>
  );
};

export default MockEMRDevTools;
